import $ from 'jquery';

import Monitoring from 'shared_modules/Monitoring';

const Vendors = (): VendorsInterface => {
  const currencyCodeListingColumnSelector = 'td[id*="Currency"] > span';
  const disabledVendorCostSelector = 'td[id^="cellItemsVendorCost"]';
  const enabledVendorCostSelector = 'input[name="vendor_cost"]';
  const formFieldHolderClass = '.form_field_holder';
  const purchaseOrderVendorCurrencySelector = '.purchase-order-vendor-currency-code > span';
  const vendorNumIdSelector = '#vendor_num_id';
  const viewAddVendorCostId = 'view_add_vendor_cost';
  const viewCalcDefaultCostSelector = '#view_calc_default_cost';
  const viewDefaultCostSelector = '#view_default_cost';
  const viewExchangeRateSelector = '#view_exchange_rate';
  const viewVendorCostSelector = '#view_vendor_cost';

  const secureFunctionBehindModule = <Args, Fn extends (...params: Args[]) => any>(
    fn: Fn
  ) => async (...args: Args[]) => {
    await new Promise(resolve => window.lsCloud.Modules.waitForModules.done(resolve));

    const hasMultiCurrencyModule = window.lsCloud.Modules.hasModule(
      'inventory.purchasing.multicurrency'
    );
    return hasMultiCurrencyModule ? fn(...args) : () => {};
  };

  /**
   * Append the vendor currency code as a `<span>` element next to an input field.
   * It is also possible to append a "What is This?" link next to the currency code.
   *
   * @param currencyCode string
   * @param hasWhatIsThisLink? boolean
   * @param targetSelector string
   */
  const appendVendorCurrencyCode = ({
    currencyCode,
    hasWhatIsThisLink = false,
    targetSelector,
  }: VendorAppendVendorCurrencyCodeInput) => {
    const targetEl = $(targetSelector);
    const targetElWrapper = targetEl.parents(formFieldHolderClass);
    const currencyCodeEl = $(`<span notranslate>(${currencyCode})</span>`);
    const whatIsThisLinkEl = $(
      '<a href="https://retail-support.lightspeedhq.com/hc/articles/1260804903129" target="_blank">What is this?</a>'
    );

    try {
      if (!currencyCode) {
        throw new Error('Currency code is missing.');
      }

      if (!targetElWrapper.length) {
        throw new Error(`${targetSelector} has no form field holder.`);
      }

      targetElWrapper.find('> span, > a').remove();
      targetElWrapper.append(currencyCodeEl);

      if (hasWhatIsThisLink) {
        targetElWrapper.append(whatIsThisLinkEl);
      }

      targetElWrapper.children().css('fontWeight', 'normal');
    } catch (error) {
      Monitoring.NRError(error, 'appendVendorCurrencyCode', { targetSelector, currencyCode });
    }
  };

  /**
   * Replace the currency code placed next to an input field label.
   *
   * @param currencyCode string
   * @param targetSelectorId string
   */
  const replaceCurrencyCodeLabel = ({
    currencyCode,
    targetSelectorId,
  }: VendorReplaceCurrencyCodeLabelInput) => {
    const targetEl = $(`label[for="${targetSelectorId}"] > span`);

    try {
      if (!currencyCode) {
        throw new Error('Currency code is missing.');
      }

      if (!targetEl.length) {
        throw new Error(`${targetSelectorId} has no label.`);
      }

      targetEl.text(`(${currencyCode})`);
    } catch (error) {
      Monitoring.NRError(error, 'replaceCurrencyCodeLabel', { targetSelectorId, currencyCode });
    }
  };

  /**
   * Fetch vendor currency data.
   *
   * @param itemID number
   * @param vendorID number
   *
   * @returns object
   */
  const fetchVendorCurrency = async ({ itemID, vendorID }: VendorsFetchVendorCurrencyInput) => {
    const apiRequest = () => {
      const queryString = itemID ? `?itemID=${itemID}` : '';
      const vendorDataUrl = `/admin/vendors/${vendorID}/currency${queryString}`;
      return window.lsCloud.getJSONFromAPI(vendorDataUrl);
    };

    if (!vendorID) {
      return {};
    }

    try {
      const response = await apiRequest();
      return response;
    } catch (error) {
      Monitoring.NRError(error, 'fetchVendorCurrency', { vendorID, itemID });
      window.alertUser('An error occurred while fetching the vendor currency.', 3);
    }

    return {};
  };

  /**
   * Read currency codes from a UI element because of some restrictions
   * on `listing_column` and `listing_column_editable` elements.
   *
   * @param sourceSelector? string
   *
   * @returns string[]
   */
  const getCurrencyCodeFromUI = (params?: VendorsGetCurrencyCodeFromUI) => {
    const sourceSelector = params?.sourceSelector || currencyCodeListingColumnSelector;
    const currencyCodes: string[] = [];

    try {
      const currencyCodesList = $(sourceSelector);

      if (!currencyCodesList.length) {
        throw new Error(`${sourceSelector} did not return any currency code.`);
      }

      currencyCodesList.each(function(_index, el) {
        currencyCodes.push($(el).text());
      });
    } catch (error) {
      Monitoring.NRError(error, 'getCurrencyCodeFromUI', { sourceSelector });
    }

    return currencyCodes;
  };

  /**
   * Update vendor fields like default cost, vendor cost, vendor ID num,
   * exchange rage in the Product > Details > Inventory Defaults page
   *
   * @param vendorData VendorData
   *
   */
  const updateProductDetailsTab = async ({
    itemID,
    useSymbolFromApi = true,
    vendorID,
  }: VendorsUpdateProductTabInput) => {
    const vendorData = await fetchVendorCurrency({
      itemID,
      vendorID,
    });

    const { vendorCurrency, vendorCost, vendorNumValue } = vendorData;

    if (!Object.keys(vendorData).length) {
      return;
    }

    try {
      const { code, rate, symbol } = vendorCurrency;

      if (!(code && rate)) {
        throw new Error(`Vendor has missing currency code or exchange rate.`);
      }

      appendVendorCurrencyCode({
        currencyCode: code,
        hasWhatIsThisLink: true,
        targetSelector: viewVendorCostSelector,
      });

      if (useSymbolFromApi && symbol) {
        window.merchantos.multiCurrency.fieldBase.replaceCurrencySymbol({
          currencySymbols: [symbol],
          targetSelector: viewVendorCostSelector,
        });
      } else {
        window.merchantos.multiCurrency.fieldBase.replaceCurrencySymbol({
          currencyCodes: [code],
          targetSelector: viewVendorCostSelector,
        });
      }

      if (vendorCost) {
        setFieldValue({
          shouldValidateMoney: true,
          targetSelector: viewVendorCostSelector,
          value: vendorCost,
        });

        setFieldValue({
          shouldValidateMoney: true,
          targetSelector: viewDefaultCostSelector,
          value: vendorCost * rate,
        });
      }

      if (vendorNumValue) {
        setFieldValue({
          targetSelector: vendorNumIdSelector,
          value: vendorNumValue,
        });
      }
    } catch (error) {
      Monitoring.NRError(error, 'updateProductDetailsTab', { vendorData });
    }
  };

  /**
   * Update vendor fields like default cost, vendor cost, exchange rate,
   * default cost in the Product > Details > VendorIDs page
   *
   * @param vendorData VendorData
   *
   */
  const updateProductVendorIDsTab = async ({
    itemID,
    useSymbolFromApi = true,
    vendorID,
  }: VendorsUpdateProductTabInput) => {
    const vendorData = await fetchVendorCurrency({
      itemID,
      vendorID,
    });

    const { vendorCurrency } = vendorData;

    if (!Object.keys(vendorData).length) {
      return;
    }

    try {
      const { code, rate, symbol } = vendorCurrency;

      if (!(code && rate)) {
        throw new Error(`Vendor has missing currency code or exchange rate.`);
      }

      replaceCurrencyCodeLabel({
        currencyCode: code,
        targetSelectorId: viewAddVendorCostId,
      });

      setFieldValue({
        targetSelector: viewExchangeRateSelector,
        value: rate.toFixed(9),
      });

      updateCost({
        currencyRateSelector: viewExchangeRateSelector,
        sourceSelector: `#${viewAddVendorCostId}`,
        targetSelector: viewCalcDefaultCostSelector,
      });

      if (useSymbolFromApi && symbol) {
        window.merchantos.multiCurrency.fieldBase.replaceCurrencySymbol({
          currencySymbols: [symbol],
          targetSelector: `#${viewAddVendorCostId}`,
        });
      } else {
        window.merchantos.multiCurrency.fieldBase.replaceCurrencySymbol({
          currencyCodes: [code],
          targetSelector: `#${viewAddVendorCostId}`,
        });
      }
    } catch (error) {
      Monitoring.NRError(error, 'updateProductVendorIDsTab', { vendorData });
    }
  };

  const updateProductInventoryTab = async ({
    defaultCost = 0,
    fieldSelectorPrefix = '',
    hasPurchasingCurrencyRates = false,
    itemID,
    useSymbolFromApi = true,
    vendorID,
  }: VendorsUpdateInventoryTabInput) => {
    const vendorData = await fetchVendorCurrency({
      itemID,
      vendorID,
    });

    const { vendorCurrency, vendorCost } = vendorData;

    if (!Object.keys(vendorData).length) {
      return;
    }

    try {
      const { code, rate, symbol } = vendorCurrency;

      if (!(code && rate)) {
        throw new Error(`Vendor has missing currency code or exchange rate.`);
      }

      const inventoryCostSelector = `#view_${fieldSelectorPrefix}_cost`;
      const inventoryCostEl = $(inventoryCostSelector);
      const vendorEl = $(`#view_${fieldSelectorPrefix}_vendor`);
      const addInventoryButtonEl = $('#addInventoryButton');

      if (!inventoryCostEl.length || !vendorEl.length) {
        throw new Error('Missing inventory cost or vendor input field.');
      }

      inventoryCostEl.prop('disabled', true);
      vendorEl.prop('disabled', true);
      addInventoryButtonEl.prop('disabled', true);

      if (hasPurchasingCurrencyRates) {
        const vendorCostId = `view_${fieldSelectorPrefix}_vendor_cost`;
        const vendorCostSelector = `#${vendorCostId}`;
        const exchangeRateSelector = `#view_${fieldSelectorPrefix}_exchange_rate`;
        const cost = vendorCost || defaultCost;

        setFieldValue({
          shouldValidateMoney: true,
          targetSelector: vendorCostSelector,
          value: cost,
        });

        setFieldValue({
          targetSelector: exchangeRateSelector,
          value: rate.toFixed(9),
        });

        setFieldValue({
          shouldValidateMoney: true,
          targetSelector: inventoryCostSelector,
          value: cost * rate,
        });

        replaceCurrencyCodeLabel({
          currencyCode: code,
          targetSelectorId: vendorCostId,
        });

        if (useSymbolFromApi && symbol) {
          window.merchantos.multiCurrency.fieldBase.replaceCurrencySymbol({
            currencySymbols: [symbol],
            targetSelector: vendorCostSelector,
          });
        } else {
          window.merchantos.multiCurrency.fieldBase.replaceCurrencySymbol({
            currencyCodes: [code],
            targetSelector: vendorCostSelector,
          });
        }
      } else {
        setFieldValue({
          shouldValidateMoney: true,
          targetSelector: inventoryCostSelector,
          value: defaultCost,
        });
      }

      inventoryCostEl.prop('disabled', false);
      vendorEl.prop('disabled', false);
      addInventoryButtonEl.prop('disabled', false);
    } catch (error) {
      Monitoring.NRError(error, 'updateProductInventoryTab', { vendorData });
    }
  };

  /**
   * Set value or text to a given selector.
   *
   * @param targetSelector string
   * @param value? string | number
   * @param text? string | number
   */
  const setFieldValue = ({
    shouldValidateMoney = false,
    targetSelector,
    text = undefined,
    value = undefined,
  }: VendorsSetFieldValue) => {
    const targetEl = $(targetSelector);
    try {
      if (!targetEl.length) {
        throw new Error(`${targetSelector} does not exist.`);
      }
      if (typeof value !== 'undefined') {
        targetEl.val(value);

        if (shouldValidateMoney) {
          window.validateMoneyForDisplay(targetEl[0] as HTMLInputElement);
        }

        return;
      }
      if (typeof text !== 'undefined') {
        targetEl.text(text);
        return;
      }
      throw new Error(`Neither value nor text was given to ${targetSelector}.`);
    } catch (error) {
      Monitoring.NRError(error, 'setFieldValue', {
        value,
        text,
        targetSelector,
      });
    }
  };

  /**
   * Update the cost of `targetSelector` using the value of `sourceSelector` times `currencyRate`.
   *
   * @param currencyRate number
   * @param sourceSelector string
   * @param targetSelector string
   */
  const updateCost = ({
    currencyRateSelector,
    sourceSelector,
    targetSelector,
  }: VendorsUpdateCostInput) => {
    try {
      const currencyRateEl = $(currencyRateSelector);
      const sourceEl = $(sourceSelector);
      const targetEl = $(targetSelector);
      const cost = parseFloat(`${sourceEl.val()}`);
      const currencyRate = parseFloat(`${currencyRateEl.val()}`);

      targetEl.val(cost * currencyRate);
      window.validateMoneyForDisplay(targetEl[0] as HTMLInputElement);
    } catch (error) {
      Monitoring.NRError(error, 'updateCost', {
        currencyRateSelector,
        sourceSelector,
        targetSelector,
      });
    }
  };

  const updatePurchaseOrderCheckInTab = () => {
    const currencyCodes = getCurrencyCodeFromUI({
      sourceSelector: purchaseOrderVendorCurrencySelector,
    });

    if ($(enabledVendorCostSelector).length) {
      window.merchantos.multiCurrency.fieldBase.replaceCurrencySymbol({
        currencyCodes,
        targetSelector: enabledVendorCostSelector,
      });
      return;
    }

    if ($(disabledVendorCostSelector).length) {
      window.merchantos.multiCurrency.fieldBaseStatic.replaceCurrencySymbol({
        currencyCodes,
        targetSelector: disabledVendorCostSelector,
      });
      return;
    }

    Monitoring.NRError(
      new Error('Could not find fields to update.'),
      'updatePurchaseOrderCheckInTab'
    );
  };

  return {
    appendVendorCurrencyCode: secureFunctionBehindModule(appendVendorCurrencyCode),
    fetchVendorCurrency: secureFunctionBehindModule(fetchVendorCurrency),
    getCurrencyCodeFromUI: secureFunctionBehindModule(getCurrencyCodeFromUI),
    replaceCurrencyCodeLabel: secureFunctionBehindModule(replaceCurrencyCodeLabel),
    setFieldValue: secureFunctionBehindModule(setFieldValue),
    updateCost: secureFunctionBehindModule(updateCost),
    updateProductDetailsTab: secureFunctionBehindModule(updateProductDetailsTab),
    updateProductInventoryTab: secureFunctionBehindModule(updateProductInventoryTab),
    updateProductVendorIDsTab: secureFunctionBehindModule(updateProductVendorIDsTab),
    updatePurchaseOrderCheckInTab: secureFunctionBehindModule(updatePurchaseOrderCheckInTab),
  };
};

export default Vendors;

window.merchantos.vendors = Vendors();
